<template>
  <div class="max-w-3xl mx-auto space-y-6 animate-[fadeIn_0.5s_ease-out] pb-12">
    <!-- 顶部用户信息卡 -->
    <div class="bg-white rounded-2xl border border-slate-100 shadow-sm p-8 text-center">
      <div class="w-20 h-20 mx-auto mb-4 rounded-full bg-[#F2C94C]/10 flex items-center justify-center border border-[#F2C94C]/20">
        <IconUser class="w-10 h-10 text-[#F2C94C]" />
      </div>
      <h2 class="text-xl font-bold text-slate-900 mb-1">管理者</h2>
      <div class="text-2xl font-black text-[#F2C94C] mb-4">
        {{ userStats.points.toLocaleString() }} 积分
      </div>
      <button
        @click="showLogoutConfirm = true"
        class="text-sm text-red-500 hover:text-red-600 font-medium px-4 py-2 rounded-lg border border-red-100 hover:bg-red-50 transition-colors"
      >
        退出登录
      </button>
    </div>

    <!-- Tab 导航 -->
    <div class="flex gap-6 border-b border-slate-100 overflow-x-auto no-scrollbar relative">
      <button
        v-for="tab in tabs"
        :key="tab.id"
        @click="activeTab = tab.id"
        :class="`pb-3 text-sm font-bold transition-all border-b-2 -mb-[1px] flex items-center gap-2 whitespace-nowrap ${activeTab === tab.id ? 'border-[#F2C94C] text-slate-900' : 'border-transparent text-slate-400 hover:text-slate-600'}`"
      >
        <component :is="tab.icon" class="w-4 h-4" />
        {{ tab.label }}
        <span v-if="tab.count !== null" :class="`ml-1 px-1.5 py-0.5 rounded-full text-[10px] ${activeTab === tab.id ? 'bg-[#F2C94C]/20 text-[#F2C94C]' : 'bg-slate-100 text-slate-400'}`">
          {{ tab.count }}
        </span>
      </button>
    </div>

    <!-- Tab 内容 -->
    <div class="py-2">
      <transition name="tab-switch" mode="out-in">
        <div :key="activeTab" class="space-y-3">
          <!-- 我的收藏 -->
          <template v-if="activeTab === 'favorites'">
            <template v-if="bookmarks.length > 0">
              <div
                v-for="item in bookmarks"
                :key="item.id"
                @click="$router.push(`/expert/${item.expertId}/case/${item.id}`)"
                class="bg-white p-4 rounded-lg border border-slate-100 shadow-sm hover:shadow-md hover:border-[#F2C94C]/40 cursor-pointer transition-all flex items-center gap-4 group"
              >
                <div class="w-12 h-12 bg-slate-50 rounded-lg flex items-center justify-center text-[#F2C94C]">
                  <IconBookmark class="w-6 h-6 fill-current" />
                </div>
                <div class="flex-1 min-w-0">
                  <h4 class="font-bold text-slate-900 mb-1 group-hover:text-[#F2C94C] transition-colors truncate">{{ item.title }}</h4>
                  <p class="text-sm text-slate-500 line-clamp-1">{{ item.summary }}</p>
                </div>
                <IconChevronRight class="w-5 h-5 text-slate-300 group-hover:text-[#F2C94C] flex-shrink-0" />
              </div>
            </template>
            <div v-else class="text-center py-16 text-slate-400">
              <IconBookmark class="w-12 h-12 mx-auto mb-3 text-slate-200" />
              <p class="text-sm">
                暂无收藏案例，浏览时点击收藏按钮即可添加
              </p>
            </div>
          </template>

          <!-- 我的贡献 -->
          <template v-if="activeTab === 'contributions'">
            <!-- 贡献统计卡片 -->
            <div class="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
              <div class="bg-[#F2C94C]/5 border border-[#F2C94C]/20 rounded-lg p-5 flex flex-col items-center text-center">
                <IconAward class="w-8 h-8 text-[#F2C94C] mb-2" />
                <div class="text-2xl font-black text-slate-900">450</div>
                <div class="text-[10px] font-bold text-slate-400 uppercase">贡献积分</div>
              </div>
              <div class="bg-blue-50 border border-blue-100 rounded-lg p-5 flex flex-col items-center text-center">
                <IconLightbulb class="w-8 h-8 text-blue-500 mb-2" />
                <div class="text-2xl font-black text-slate-900">3</div>
                <div class="text-[10px] font-bold text-slate-400 uppercase">建议提交</div>
              </div>
              <div class="bg-green-50 border border-green-100 rounded-lg p-5 flex flex-col items-center text-center">
                <IconHeart class="w-8 h-8 text-green-500 mb-2" />
                <div class="text-2xl font-black text-slate-900">46</div>
                <div class="text-[10px] font-bold text-slate-400 uppercase">获得赞同</div>
              </div>
            </div>

            <div v-for="item in contributions" :key="item.id" class="bg-white p-4 rounded-lg border border-slate-100 shadow-sm">
              <div class="flex items-center gap-2 mb-2">
                <span class="text-xs font-bold text-slate-500">{{ item.topic }}</span>
                <span :class="`text-[10px] px-1.5 py-0.5 rounded ${item.status === 'approved' ? 'bg-green-50 text-green-600' : 'bg-amber-50 text-amber-600'}`">
                  {{ item.status === 'approved' ? '已通过' : '审核中' }}
                </span>
              </div>
              <p class="text-sm text-slate-700 leading-relaxed">{{ item.content }}</p>
              <div class="flex items-center gap-4 mt-3 text-[11px] text-slate-400">
                <span>{{ item.date }}</span>
                <span class="flex items-center gap-1">
                  <IconHeart class="w-3 h-3" /> {{ item.likes }}
                </span>
                <span class="flex items-center gap-1">
                  <IconAward class="w-3 h-3" /> +{{ item.points }}
                </span>
              </div>
            </div>
          </template>

          <!-- 账号设置 -->
          <template v-if="activeTab === 'settings'">
            <div class="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
              <h3 class="text-base font-bold text-slate-900 mb-4 flex items-center gap-2">
                <IconLock class="w-4 h-4 text-[#F2C94C]" />
                修改密码
              </h3>
              <form @submit.prevent="handlePasswordSubmit" class="space-y-4">
                <div>
                  <label class="block text-xs font-medium text-slate-500 mb-1.5">当前密码</label>
                  <input
                    type="password"
                    v-model="passwordForm.current"
                    class="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-900 focus:border-[#F2C94C] outline-none transition-all"
                    placeholder="请输入当前密码"
                  />
                </div>
                <div>
                  <label class="block text-xs font-medium text-slate-500 mb-1.5">新密码</label>
                  <input
                    type="password"
                    v-model="passwordForm.new"
                    class="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-900 focus:border-[#F2C94C] outline-none transition-all"
                    placeholder="请输入新密码"
                  />
                </div>
                <div>
                  <label class="block text-xs font-medium text-slate-500 mb-1.5">确认新密码</label>
                  <input
                    type="password"
                    v-model="passwordForm.confirm"
                    class="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-900 focus:border-[#F2C94C] outline-none transition-all"
                    placeholder="请再次输入新密码"
                  />
                </div>

                <p v-if="passwordError" class="text-xs text-red-500">{{ passwordError }}</p>
                <p v-if="passwordSuccess" class="text-xs text-green-600">{{ passwordSuccess }}</p>

                <div class="flex gap-3 pt-2">
                  <button
                    type="button"
                    @click="passwordForm = { current: '', new: '', confirm: '' }"
                    class="px-5 py-2.5 border border-slate-200 text-slate-600 text-sm font-medium rounded-xl hover:bg-slate-50 transition-colors"
                  >
                    取消
                  </button>
                  <button
                    type="submit"
                    class="px-5 py-2.5 bg-[#F2C94C] text-white text-sm font-medium rounded-xl hover:bg-[#E5B73B] transition-colors"
                  >
                    确认修改
                  </button>
                </div>
              </form>
            </div>
          </template>
        </div>
      </transition>
    </div>

    <!-- 退出登录确认弹窗 -->
    <transition name="fade">
      <div
        v-if="showLogoutConfirm"
        class="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
        @click="showLogoutConfirm = false"
      >
        <transition name="scale">
          <div
            v-if="showLogoutConfirm"
            @click.stop
            class="bg-white rounded-2xl p-6 max-w-sm w-full shadow-xl"
          >
            <div class="flex items-center gap-3 mb-4">
              <div class="w-10 h-10 bg-red-50 rounded-full flex items-center justify-center">
                <IconTriangleAlert class="w-5 h-5 text-red-500" />
              </div>
              <h3 class="text-lg font-bold text-slate-900">确认退出登录？</h3>
            </div>
            <p class="text-sm text-slate-500 mb-6">
              退出后需要重新登录才能访问个人数据。
            </p>
            <div class="flex gap-3">
              <button
                @click="showLogoutConfirm = false"
                class="flex-1 py-2.5 border border-slate-200 text-slate-600 text-sm font-medium rounded-lg hover:bg-slate-50 transition-colors"
              >
                取消
              </button>
              <button
                @click="handleLogout"
                class="flex-1 py-2.5 bg-red-500 text-white text-sm font-medium rounded-lg hover:bg-red-600 transition-colors"
              >
                确认退出
              </button>
            </div>
          </div>
        </transition>
      </div>
    </transition>
  </div>
</template>

<script>
import {
  IconUser,
  IconChevronRight,
  IconBookmark,
  IconAward,
  IconHeart,
  IconLightbulb,
  IconLock,
  IconTriangleAlert,
} from '../components/icons'
import { EXPERT_CASES } from '../data'

export default {
  name: 'ProfilePage',

  components: {
    IconUser,
    IconChevronRight,
    IconBookmark,
    IconAward,
    IconHeart,
    IconLightbulb,
    IconLock,
    IconTriangleAlert,
  },

  data() {
    return {
      activeTab: 'favorites',
      showLogoutConfirm: false,
      passwordForm: {
        current: '',
        new: '',
        confirm: '',
      },
      passwordError: '',
      passwordSuccess: '',
    }
  },

  computed: {
    userStats() {
      return this.$store.state.userStats
    },

    contributions() {
      return this.$store.state.contributions
    },

    bookmarks() {
      return Object.values(EXPERT_CASES).filter(c => this.userStats.bookmarks?.includes(c.id))
    },

    tabs() {
      return [
        { id: 'favorites', label: '我的收藏', icon: 'IconBookmark', count: this.bookmarks.length },
        { id: 'contributions', label: '我的贡献', icon: 'IconAward', count: this.contributions.length },
        { id: 'settings', label: '账号设置', icon: 'IconLock', count: null },
      ]
    },
  },

  methods: {
    handleLogout() {
      this.$store.commit('SET_IS_LOGGED_IN', false)
      this.$router.push('/')
    },

    handlePasswordSubmit() {
      this.passwordError = ''
      this.passwordSuccess = ''

      if (!this.passwordForm.current || !this.passwordForm.new || !this.passwordForm.confirm) {
        this.passwordError = '请填写所有密码字段'
        return
      }
      if (this.passwordForm.new !== this.passwordForm.confirm) {
        this.passwordError = '新密码与确认密码不一致'
        return
      }
      if (this.passwordForm.new.length < 6) {
        this.passwordError = '新密码长度不能少于6位'
        return
      }

      // mock 成功
      this.passwordSuccess = '密码修改成功'
      this.passwordForm = { current: '', new: '', confirm: '' }
    },
  },
}
</script>

<style scoped>
.tab-switch-enter-active,
.tab-switch-leave-active {
  transition: all 0.2s ease;
}
.tab-switch-enter {
  opacity: 0;
  transform: translateY(10px);
}
.tab-switch-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.scale-enter-active,
.scale-leave-active {
  transition: all 0.2s ease;
}
.scale-enter {
  opacity: 0;
  transform: scale(0.95);
}
.scale-leave-to {
  opacity: 0;
  transform: scale(0.95);
}
</style>
